/**
 * Продуктовые константы РФ-версии.
 *
 * Источник каждой цифры — seeno-product-spec.md; номер раздела указан рядом.
 * Ни одна поверхность не пишет цену или квоту буквой в разметке: разъехавшиеся
 * числа на витрине и в кабинете — это разговор с клиентом о том, за что он
 * заплатил.
 */

import { countOf, ladderProse } from './format';

/**
 * Движки в фиксированном порядке, Нейро первым (voice.md, спека §7).
 * `Нейро Яндекс` и `Яндекс Нейро` — запрещённые формы.
 */
export const ENGINES = [
  'Нейро',
  'ChatGPT',
  'Perplexity',
  'Gemini',
  'DeepSeek',
  'Qwen',
  'Kimi',
  'Обзор от Google',
  'YandexGPT',
  'Поиск Яндекса',
] as const;

/**
 * Код движка → название, на случай когда справочник `GET /engines` не доехал.
 *
 * Названия по-прежнему приходят справочником, и этот список ему не замена: он
 * нужен ровно там, где раньше в таблицу падал сырой код. Строка `chatgpt` в
 * колонке «Движок» — не название, а имя ключа в чужой базе, и особенно скверно
 * она смотрелась на ПУБЛИЧНОЙ странице отчёта, где справочника нет вовсе.
 *
 * Коды — из `catalog.engines` (миграции `seeno-ru-api`). Незнакомый код сюда не
 * попадёт: для него остаётся общее «Движок» без выдумывания имени.
 */
export const ENGINE_TITLE_BY_CODE: Record<string, string> = {
  neuro: 'Нейро',
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  google_aio: 'Обзор от Google',
  deepseek: 'DeepSeek',
  qwen: 'Qwen',
  kimi: 'Kimi',
  gemini: 'Gemini',
  yandexgpt: 'YandexGPT',
  yandex_search: 'Поиск Яндекса',
};

/** Название движка по коду: справочник, затем запасной список, затем нейтральное слово. */
export function engineName(code: string, fromCatalogue?: string | null): string {
  return fromCatalogue || ENGINE_TITLE_BY_CODE[code] || 'Движок';
}

/**
 * Набор бесплатного аудита — ровно те движки, которые включены в справочнике
 * `catalog.engines`: аудит запускает ВСЕ активные движки, а не отдельный список.
 *
 * До 10 августа 2026 здесь стояли «Gemini» и «Поиск Яндекса», которых аудит не
 * запускал: Gemini был строкой справочника без адаптера, у «Поиска Яндекса»
 * адаптера нет до сих пор. Perplexity и «Обзор от Google», наоборот, работали и
 * в списке не значились. Эта строка уезжает в первый экран, в подвал и в
 * картинку для соцсетей — то есть мы обещали покупателю замер в движках,
 * которых не запускали, продуктом, который меряет чужую честность.
 *
 * Правило: движок попадает сюда, когда у него есть адаптер в образе сканера И
 * `is_active` в справочнике. Не раньше.
 */
export const AUDIT_ENGINES = [
  'Нейро',
  'ChatGPT',
  'Perplexity',
  'Обзор от Google',
  'DeepSeek',
  'Qwen',
  'Kimi',
  'Gemini',
] as const;

/** Полоса подвала. Перечислять движки, которых мы в этом контексте не запускаем, нельзя. */
export const FOOTER_STRIP = `Меряем ${AUDIT_ENGINES.join(' · ')}`;

/** Бесплатный аудит (спека §4). */
export const AUDIT = {
  questions: 30,
  engines: AUDIT_ENGINES.length,
  duration: '2–4 минуты',
  /** Сколько дней открыта публичная ссылка на отчёт. */
  publicDays: 7,
} as const;

/**
 * Срок жизни публичной ссылки — одной строкой.
 *
 * Он был написан четырьмя разными способами на четырёх экранах: «Публичный
 * отчёт живёт семь дней», «Отчёт живёт семь дней», «Публичная ссылка
 * бесплатного аудита живёт семь дней», «Этому отчёту больше семи дней». Срок
 * при этом договорный — он записан в оферте (`landing/src/pages/terms.astro`,
 * §5), и расхождение здесь стоит дороже стилистики.
 */
export const PUBLIC_REPORT_LIFETIME = `Публичный отчёт живёт ${String(AUDIT.publicDays)} дней.`;

/**
 * Сколько раз мы задаём один вопрос.
 *
 * Механика замера, а не оборот: два прогона нужны, чтобы было с чем сравнить
 * ответ, — отсюда «сходимость». Написана она была шестью разными способами на
 * пяти экранах.
 */
export const DOUBLE_RUN = 'Каждый вопрос уходит каждому движку дважды.';

/**
 * От чего зависит размер ядра.
 *
 * Пять редакций одной мысли: «Размер ядра определяется тарифом, а не
 * фиксированным числом», «размер ядра определяется тарифом», «сколько их
 * отслеживать, определяет тариф». Отвечают все на один вопрос человека — сколько
 * вопросов мне можно.
 */
export const CORE_SIZE_RULE = 'Размер ядра определяет тариф.';

/**
 * Что делает ответ органическим.
 *
 * Правило замера, а не оборот речи: упоминание в ответе на вопрос, где имя
 * бренда названо прямо, ничего не доказывает. Написано в трёх местах — на
 * синонимах, при заведении бренда и в сноске под цитатой, — и все три обязаны
 * говорить одно.
 */
export const ORGANIC_RULE =
  'Ответ считается органическим, только если имени бренда нет в самом вопросе.';

/**
 * Реквизиты в выставленном документе.
 *
 * Пять редакций одной мысли, две из них на одном экране оплаты. Смысл всюду
 * один: документ — снимок на момент выписки.
 */
export const PAYER_SNAPSHOT =
  'Реквизиты в документе — копия на момент выписки: организация переедет, а подписанный документ останется прежним.';

/**
 * Сколько ждать, когда вопросы придумывает модель. Это НЕ длительность аудита:
 * там опрашиваются все движки, здесь один раз спрашивается генератор. Оба срока
 * висят на экране перед кнопкой, и подставить один вместо другого — значит
 * пообещать не тот срок; поэтому у каждого своя строка, а не общая.
 */
export const GENERATION_DURATION = 'до двух минут';

export interface Tier {
  /** Ключ тарифа, латиницей — не показывается клиенту. */
  key: string;
  name: string;
  /** Рублей в месяц. Целые рубли; в контракте деньги — копейки. */
  monthly: number;
  /** Рублей в год; годовая цена = десять месячных, два месяца в подарок. */
  yearly: number | null;
  questions: number;
  workspaces: string;
  competitors: string;
  history: string;
  description: string;
  /** Отмечен как рекомендуемый. Ровно один на сетку. */
  featured?: boolean;
  /** Верхние тарифы идут полосой во всю ширину: пять карточек в 1180px не встают. */
  wide?: boolean;
  /**
   * Цена ждёт подтверждения фаундера (спека §10, defects.md часть 0).
   *
   * На витрину при этом идёт **значение из спеки**, а не прочерк: промежуточное
   * указание дизайн-системы — показывать цифры §10 с пометкой, что они
   * согласуются. Пометка стоит сноской под сеткой, одна на блок.
   */
  pending?: boolean;
  /**
   * Цена открытая: «от 199 000 ₽». Свойство отдельно от `pending`, потому что
   * это разные вещи. У Партнёра в спеке §10 стоит точная пара 99 000 / 990 000,
   * и «от» рядом с точной годовой ценой в том же списке — это два разных
   * ответа на один вопрос в одной карточке.
   */
  from?: boolean;
}

/** Тарифы РФ, спека §10. Пробного периода нет — решение фаундера 8 августа 2026. */
export const TIERS: Tier[] = [
  {
    key: 'start',
    name: 'Старт',
    monthly: 9900,
    yearly: 99000,
    questions: 50,
    workspaces: '1 рабочее пространство',
    competitors: '10 конкурентов',
    history: 'история 6 месяцев',
    description: 'Одна компания, один город, регулярный замер вместо догадок.',
  },
  {
    key: 'growth',
    name: 'Рост',
    monthly: 24900,
    yearly: 249000,
    questions: 150,
    workspaces: '3 рабочих пространства',
    competitors: '25 конкурентов',
    history: 'история 12 месяцев',
    description: 'Несколько брендов или городов и разбор по конкурентам.',
    featured: true,
  },
  {
    key: 'scale',
    name: 'Масштаб',
    monthly: 49900,
    yearly: 499000,
    questions: 400,
    workspaces: '10 рабочих пространств',
    competitors: '50 конкурентов',
    history: 'история 24 месяца',
    // «Доступ к данным на чтение» (спека §10) с витрины снят до решения
    // фаундера: механика не описана нигде — API, выгрузка или роль на
    // просмотр, — а покупатель перечитывает строку дважды и не понимает её.
    // Назвать механику наугад значило бы продать то, чего может не быть.
    description: 'Много городов и направлений, отчёты под своим брендом.',
  },
  {
    key: 'partner',
    name: 'Партнёр',
    monthly: 99000,
    yearly: 990000,
    questions: 800,
    // Термин один на весь продукт — «рабочее пространство» (глоссарий
    // voice.md). Агентская специфика живёт в пояснении, а не во втором слове
    // для той же сущности: «клиентское пространство» рядом с «рабочим» в одной
    // сетке читается как две разные вещи, которых в продукте нет.
    workspaces: '25 рабочих пространств — по одному на клиента',
    competitors: 'конкуренты без лимита',
    history: 'история 36 месяцев',
    description:
      'Агентствам, которые работают сами: общий пул вопросов и портал на своём домене.',
    wide: true,
    pending: true,
  },
  {
    key: 'managed',
    name: 'Управляемая аналитика',
    monthly: 199000,
    yearly: null,
    questions: 1000,
    workspaces: 'пространства по договорённости',
    competitors: 'конкуренты без лимита',
    history: 'история 60 месяцев',
    description:
      'Тот же Seeno Studio для сети с десятками точек или агентства с портфелем клиентов.',
    wide: true,
    pending: true,
    from: true,
  },
];

/**
 * Seeno Studio — сервисное крыло продукта: команда сама работает с сайтом,
 * отзывами, подборками и упоминаниями, платформа показывает изменения.
 *
 * Цена открытая («от»): объём работы зависит от исходной ситуации. Утверждена
 * редизайном главной, который фаундер выпустил на прод seeno.ru в августе 2026.
 * Объём проверок — как в тарифе Масштаб (`TIERS`, ключ `scale`).
 */
export const STUDIO = {
  name: 'Seeno Studio',
  monthly: 80000,
  from: true,
  /** Минимальный срок в месяцах: нейросетям нужно время увидеть обновлённые сведения. */
  minTermMonths: 3,
  /** Одной строкой — для структурированных данных, как `description` у тарифов. */
  description:
    'Команда Seeno сама работает с сайтом, отзывами, подборками и упоминаниями, платформа показывает изменения.',
} as const;

/**
 * Минимальный срок Seeno Studio словами — одна формулировка на все поверхности.
 * До этой константы витрина писала «минимум квартал», а FAQ — «минимум три
 * месяца»: два разных ответа на один вопрос о сроке одного и того же сервиса.
 */
export const STUDIO_MIN_TERM = `минимум ${countOf(STUDIO.minTermMonths, 'месяц', 'месяца', 'месяцев')}`;

/**
 * Докупки, только на действующей платной подписке (спека §10).
 *
 * «В недельную перепись» из названий убрано: уточнение отличало эти позиции от
 * докупок ежедневного пульса, а пульса в продукте больше нет. Оставшись без
 * пары, оно называет покупателю режим замера в тот момент, когда он выбирает
 * объём, — и заставляет гадать, бывает ли другой. Названия в каталоге
 * `seeno-ru-api` придётся привести к этим же: их печатает кабинет.
 */
export const ADDONS = [
  { name: '+50 отслеживаемых вопросов', monthly: 4900 },
  { name: '+200 отслеживаемых вопросов', monthly: 15900 },
  { name: '+5 рабочих пространств, только Партнёр', monthly: 5900 },
];

/** Вертикали РФ (спека §3). Словарь значений `data-industry` фиксирован. */
export const INDUSTRIES = [
  {
    slug: 'auto_dealers',
    name: 'Автодилеры',
    sub: 'Дилерским центрам и группам',
    questions: [
      'Каким автодилерам в Казани можно доверять?',
      'Где выгоднее купить кроссовер в кредит?',
    ],
  },
  {
    slug: 'furniture',
    name: 'Мебель на заказ',
    sub: 'Производствам и салонам',
    questions: [
      'Кто делает кухни на заказ в Москве?',
      'Какой шкаф-купе заказать в квартиру-студию?',
    ],
  },
] as const;

/**
 * Открытые исследования рынков — раздел витрины `/research/`.
 *
 * Параметры замера записаны литералами, а не производными от продуктовых
 * констант: исследование — зафиксированный выпуск, и его числа не меняются
 * вместе с набором движков аудита. Пока выпуск не опубликован, поверхность
 * показывает только анонс — карточка без единого выдуманного числа. Поэтому
 * здесь только входные параметры замера (вопросы, движки): число ответов —
 * результат, и до публикации его нет.
 */
export const RESEARCH = {
  /** Первый выпуск. До публикации известен только месяц. */
  dental: {
    title: 'Стоматологии Москвы и Петербурга',
    questions: 400,
    engines: 8,
    publication: 'август 2026',
  },
} as const;

/**
 * Метрики, которые получает клиент (спека §8). Витрина называет их теми же
 * словами, что и кабинет: расхождение названий на витрине и на экране — это
 * разговор с клиентом о том, за что он заплатил.
 */
export const METRICS = [
  {
    label: 'Видимость',
    hint: 'Доля органических ответов, где движок назвал вас. Одно число, а не две метрики.',
  },
  {
    label: 'Индекс ИИ-видимости',
    hint: `Сводный балл 0–100 и статус словом: ${ladderProse()}. Считается одной формулой на все поверхности.`,
  },
  {
    label: 'Позиция в ответе',
    hint: 'Как часто вы в первой тройке, как часто названы первым и какая позиция в среднем.',
  },
  {
    label: 'Тональность и свойства',
    // Стрелка внутри обычного предложения запрещена: `→ ↑ ↓` живут только в
    // моношрифтовых подписях, а из основной гарнитуры знак приходит системным
    // запасным шрифтом и сидит мимо базовой линии (foundations.md).
    hint: 'Не только «назвали», но и что именно сказали: свойство и оценка к нему, плюс общий тон по каждому движку.',
  },
  {
    label: 'Источники',
    hint: 'Домены, на которые движки ссылаются в вашей категории. Источники отдают не все движки, и разрез это показывает.',
  },
  {
    label: 'Индекс AI-готовности сайта',
    hint: 'Пускает ли сайт роботов ИИ, есть ли разметка и страница вопросов-ответов, видят ли вас справочники. Работает без замера.',
  },
] as const;

/**
 * Рекомендации (спека §9). На витрине показаны типы, а не выдуманный список
 * клиента: одновременно активных не больше двенадцати, часть продукт
 * перепроверяет за вас и закрывает сам.
 */
export const RECOMMENDATIONS = [
  'Открыть сайт роботам нейросетей и поисковых интерфейсов и убедиться, что их больше ничего не отсекает.',
  'Разметить страницы структурированными данными: движок цитирует то, что разбирается машиной.',
  'Завести страницу вопросов-ответов на языке покупателя, а не на языке отдела маркетинга.',
  'Появиться в источниках, на которые движки ссылаются в вашей категории, — обычно это не ваш сайт.',
  'Закрыть карточки в справочниках и на площадках отзывов: ответ собирается из чужих страниц чаще, чем из вашей.',
] as const;

/**
 * Слова важности оповещения — одна колонка, одна грамматика.
 *
 * `Критично` задано дизайн-системой (app-kit.md §4). Две другие ступени там не
 * названы, и до этого места они набирались полными прилагательными женского
 * рода — «высокая», «средняя», — из-за чего в столбце, который клиент читает
 * сверху вниз, соседствовали краткая форма среднего рода и два полных
 * прилагательных. Приводим все три к форме, которую задала верхняя ступень.
 *
 * design: derived, not documented — грамматика второй и третьей ступени.
 *
 * Ниже средней у клиента ступени нет: четвёртый уровень §9 — это канал команды,
 * и до интерфейса он доезжает как средняя (app-kit.md §4).
 */
export const SEVERITY_WORDS = {
  critical: 'Критично',
  high: 'Важно',
  medium: 'Умеренно',
  low: 'Умеренно',
} as const;

/** Техническое — это отказ источника, а не падение бренда (app-kit.md §4). */
export const TECHNICAL_WORD = 'Техническое';

/** Оповещения (спека §9). Важность — тем же словом, каким её присвоило правило. */
export const ALERTS = [
  { rule: 'Бренд исчез из движка', level: SEVERITY_WORDS.critical },
  { rule: 'Доля голоса упала больше чем на 25 п. п.', level: SEVERITY_WORDS.critical },
  { rule: 'В ответах появился новый конкурент', level: SEVERITY_WORDS.high },
  {
    rule: 'Движок отвечает нулём при норме по остальным',
    level: `${SEVERITY_WORDS.high} · ${TECHNICAL_WORD}`,
  },
  { rule: 'Ухудшилась средняя позиция', level: SEVERITY_WORDS.medium },
] as const;

/**
 * Вопросы и ответы. Один источник для разметки страницы и для JSON-LD FAQPage:
 * разошедшийся ответ в структурированных данных — ровно тот дефект, который мы
 * продаём находить у других.
 */
export const FAQ = [
  {
    q: 'Что проверяет Seeno?',
    a: `Вопросы ваших покупателей мы задаём ${countOf(AUDIT_ENGINES.length, 'нейросети', 'нейросетям', 'нейросетям')} и читаем ответы целиком. Называют ли вас, кто стоит рядом, на какие источники нейросеть сослалась.`,
  },
  {
    q: 'Гарантируете первое место в ответе?',
    a: 'Нет, никто не торгует местами в ответах. Отвечаем за другое: правим источники, замеряем каждую неделю и показываем в отчёте, что сдвинулось.',
  },
  {
    q: 'А если мои покупатели ещё не спрашивают нейросети?',
    a: 'Точной доли не знает никто, и мы её не выдумываем. Но что нейросети отвечают про вас сегодня, покажет бесплатный аудит. Если вас называют и так, отчёт это покажет, и платить будет не за что.',
  },
  {
    q: 'А если я сам спрошу нейросеть про свою компанию?',
    a: `Так и сделайте, это полезно. Только один ответ одной нейросети сегодня ничего не показывает: завтра список будет другим. Мы задаём десятки вопросов ${countOf(AUDIT_ENGINES.length, 'нейросети', 'нейросетям', 'нейросетям')} каждую неделю и сравниваем замеры — видно движение, а не случайность.`,
  },
  {
    q: 'Чем это отличается от продвижения в обычном поиске?',
    a: 'Поиск отдаёт список ссылок, нейросеть — готовый ответ с несколькими компаниями. Мы работаем с источниками, из которых нейросеть собрала ответ.',
  },
  {
    q: 'Когда появятся изменения?',
    a: `Зависит от того, сколько источников нужно привести в порядок. Поэтому Seeno Studio работает ${STUDIO_MIN_TERM}. Замер идёт каждую неделю, так что движение видно по ходу, а не в конце срока.`,
  },
  {
    q: 'С чего начать и нужны ли доступы?',
    a: 'С бесплатного аудита: адрес сайта и рабочая почта, куда прислать отчёт. Доступы к сайту и счётчикам для него не нужны.',
  },
  {
    q: 'Что такое отслеживаемый вопрос?',
    a: `Вопрос покупателя, который мы задаём нейросетям каждую неделю. Например: «${INDUSTRIES[1].questions[0]}» Чем больше таких вопросов, тем полнее картина и тем дороже тариф.`,
  },
] as const;

/**
 * Реквизиты (спека §14.1). Идут в подвал, в оферту и в политику.
 * Почтовый адрес в публичных документах не раскрывается — только почта.
 */
export const LEGAL_ENTITY = {
  full: 'Индивидуальный предприниматель Тарасов Павел Борисович',
  /**
   * Родительный и дательный падежи реквизитов.
   *
   * Подстановка именительного падежа посреди предложения читается как ошибка
   * машины и подрывает доверие ровно к тем страницам, где оно нужнее всего:
   * «даю согласие Индивидуальный предприниматель Тарасов Павел Борисович», «…
   * принадлежащих Индивидуальный предприниматель Тарасов Павел Борисович».
   * Склонять программно нечем и не нужно — форм ровно три, и они записаны.
   */
  genitive: 'Индивидуального предпринимателя Тарасова Павла Борисовича',
  dative: 'Индивидуальному предпринимателю Тарасову Павлу Борисовичу',
  short: 'ИП Тарасов П. Б.',
  inn: '772799904950',
  ogrnip: '324774600236018',
  /**
   * Печатается в подвале КАЖДОЙ страницы лендинга, включая юридические.
   *
   * Здесь стояло «УСН, НДС не облагается». Безусловной формулой это перестанет
   * быть правдой ровно в день превышения порога выручки: с 2025 года УСН —
   * плательщик НДС с освобождением по порогу. Хуже, что оферту от этой формулы
   * уже отвязали, а подвал печатал её под исправленным текстом. Обещаем то, что
   * от порога не зависит и что для покупателя и есть суть: цена конечная.
   */
  taxation: 'УСН. Цены конечные, НДС сверху не начисляется',
  contact: 'help@seeno.ru',
  /**
   * Печатная и машинная формы записаны отдельно по той же причине, что и
   * падежи выше: собирать одну из другой заменой символов — лишний код ради
   * двух констант. Телефон стоит в подвале рядом с почтой и Telegram —
   * модерация ЮKassa смотрит на телефон на сайте. Снятым он побыл несколько
   * часов: 14 августа 2026 его убрали в пользу Telegram и в тот же день
   * вернули решением владельца. Telegram и телефон не заменяют друг друга.
   */
  phone: '+7 925 411-22-85',
  phoneHref: 'tel:+79254112285',
  /**
   * Телеграм для разговора о работе: кнопки «Обсудить» на главной и ссылка
   * «Написать в Telegram» в подвале ведут сюда, а не на почту. Почта осталась
   * там, где обращение обязано быть письменным, — в подвале и в юридических
   * страницах.
   *
   * Хранится только ссылка: имя учётной записи нигде не печатается, и вторая
   * константа с тем же `anton_kuznetsov8` разъехалась бы с первой при первой же
   * смене адресата. Понадобится печатная форма — она выводится отсюда, как
   * телефон выше, а не заводится рядом.
   *
   * Ссылка по юзернейму, а не по номеру: `t.me/+7…` Telegram показывает как
   * приглашение в группу и открывает только при разрешённом поиске по номеру.
   */
  telegramHref: 'https://t.me/anton_kuznetsov8',
} as const;

/**
 * Четыре юридические страницы (спека §14.2). Ссылки на все четыре — в подвале.
 *
 * Слаг называет то, что по нему лежит. Приёмка нашла обратное: `/privacy/`
 * открывал согласие на обработку, а политика конфиденциальности пряталась за
 * `/legal/`. Для документов, на которые ссылаются из формы регистрации и из
 * оформления оплаты, это не косметика: человек переходит по ссылке «политика
 * конфиденциальности» и попадает в текст согласия, а даёт он их в разные
 * моменты и на разных основаниях.
 */
export const LEGAL_PAGES = [
  { href: '/privacy/', title: 'Политика конфиденциальности' },
  { href: '/legal/', title: 'Согласие на обработку персональных данных' },
  { href: '/license/', title: 'Публичная оферта' },
  { href: '/terms/', title: 'Пользовательское соглашение' },
] as const;

/** Разделы кабинета (спека §4, названия — voice.md). */
export const CABINET_SECTIONS = [
  'Обзор бренда',
  'Портфель',
  'Требует внимания',
  'Движки',
  'Конкуренты',
  'Семантическое ядро',
  'Рекомендации',
  'Планы действий',
  'Источники',
  'Оповещения',
  'Центр отчётов',
  'Отчёты под своим брендом',
  'Тариф',
] as const;
